/**
 * SS Data Display Components — DataTable, InfoGrid, Pagination, StatBar
 * ─────────────────────────────────────────────────────────────────────────────
 * Usage:
 *   import { DataTable, InfoGrid, Pagination, StatBar } from './data';
 */
import React, { useState } from 'react';
import { COLOR, FONT, RADIUS, thStyle, tdStyle } from './tokens';
import { EmptyState, Spinner } from './feedback';

// ── DataTable ─────────────────────────────────────────────────────────────────
/**
 * A generic sortable table with row selection and hover states.
 *
 * columns: Array<{
 *   key:       string,               // data key (also used as sort key)
 *   label:     string,               // header label
 *   render?:   (value, row) => node, // custom cell renderer
 *   sortable?: boolean,
 *   width?:    string | number,
 *   align?:    'left' | 'center' | 'right',
 * }>
 *
 * rows:        Array<object>
 * rowKey:      string | (row) => string   — unique key prop
 * onRowClick?: (row) => void
 * selectedKey? string | number            — highlights matching rowKey
 * loading?:    boolean
 * emptyIcon?:  string
 * emptyTitle?: string
 * emptyMsg?:   string
 */
export function DataTable({
  columns,
  rows,
  rowKey = 'id',
  onRowClick,
  selectedKey,
  loading,
  emptyIcon,
  emptyTitle = 'No records found',
  emptyMsg,
  style: extra,
  minWidth,
}) {
  const [sortKey, setSortKey]   = useState(null);
  const [sortDir, setSortDir]   = useState('asc');

  const getKey = row => typeof rowKey === 'function' ? rowKey(row) : row[rowKey];

  const toggleSort = key => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const sorted = (() => {
    if (!sortKey) return rows;
    return [...rows].sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp = typeof av === 'string' ? av.localeCompare(bv) : av - bv;
      return sortDir === 'asc' ? cmp : -cmp;
    });
  })();

  if (loading) return <Spinner />;

  return (
    <div style={{ overflowX: 'auto', ...extra }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth }}>
        <thead>
          <tr>
            {columns.map(col => (
              <th
                key={col.key}
                onClick={col.sortable ? () => toggleSort(col.key) : undefined}
                style={{
                  ...thStyle,
                  width:  col.width,
                  cursor: col.sortable ? 'pointer' : 'default',
                  userSelect: 'none',
                  textAlign: col.align || 'left',
                }}
              >
                {col.label}
                {col.sortable && sortKey === col.key && (
                  <span style={{ marginLeft: 4, opacity: 0.7 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 ? (
            <tr>
              <td colSpan={columns.length} style={{ padding: 0, border: 'none' }}>
                <EmptyState icon={emptyIcon} title={emptyTitle} message={emptyMsg} />
              </td>
            </tr>
          ) : sorted.map(row => {
            const key     = getKey(row);
            const active  = selectedKey != null && key === selectedKey;
            return (
              <tr
                key={key}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                style={{
                  cursor:     onRowClick ? 'pointer' : 'default',
                  background: active ? COLOR.blueBg : 'transparent',
                  transition: 'background .1s',
                }}
                onMouseEnter={e => { if (!active && onRowClick) e.currentTarget.style.background = COLOR.grayBg; }}
                onMouseLeave={e => { e.currentTarget.style.background = active ? COLOR.blueBg : 'transparent'; }}
              >
                {columns.map(col => (
                  <td
                    key={col.key}
                    style={{ ...tdStyle, textAlign: col.align || 'left' }}
                  >
                    {col.render ? col.render(row[col.key], row) : (row[col.key] ?? '—')}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── InfoGrid ──────────────────────────────────────────────────────────────────
/**
 * Key/value grid for detail panels.
 *
 * rows: Array<[label: string, value: any, options?: { color?: string, mono?: boolean }]>
 *
 * <InfoGrid rows={[
 *   ['User ID', 42],
 *   ['Status',  'Active', { color: COLOR.green }],
 * ]} />
 */
export function InfoGrid({ rows, style: extra }) {
  const validRows = rows.filter(([, v]) => v != null && v !== '');
  if (validRows.length === 0) return null;

  return (
    <div style={{ display: 'grid', rowGap: 10, ...extra }}>
      {validRows.map(([label, value, opts = {}]) => (
        <div key={label} style={{ display: 'flex', gap: 12, fontSize: FONT.size.base }}>
          <span style={{ color: COLOR.gray, fontWeight: FONT.weight.semi, minWidth: 130, flexShrink: 0 }}>
            {label}
          </span>
          <span style={{
            color:      opts.color || COLOR.text,
            fontFamily: opts.mono ? 'monospace' : FONT.family,
            wordBreak:  'break-all',
          }}>
            {String(value)}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── Pagination ────────────────────────────────────────────────────────────────
/**
 * @param {number}                page    - current 1-based page
 * @param {number}                total   - total record count
 * @param {number}                per     - records per page
 * @param {(page: number) => void} onPage
 */
export function Pagination({ page, total, per, onPage }) {
  const pages = Math.ceil(total / per);
  if (pages <= 1) return null;

  const btnStyle = disabled => ({
    padding:      '6px 14px',
    background:   '#fff',
    border:       `1px solid ${COLOR.grayBorder}`,
    borderRadius: RADIUS.md,
    cursor:       disabled ? 'not-allowed' : 'pointer',
    fontSize:     FONT.size.base,
    color:        disabled ? COLOR.grayLight : COLOR.gray,
    fontFamily:   FONT.family,
    opacity:      disabled ? 0.5 : 1,
  });

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 18 }}>
      <button style={btnStyle(page === 1)} disabled={page === 1}
        onClick={() => onPage(Math.max(1, page - 1))}>‹ Prev</button>
      <span style={{ fontSize: FONT.size.base, color: COLOR.textMuted, minWidth: 100, textAlign: 'center' }}>
        Page {page} / {pages}
      </span>
      <button style={btnStyle(page === pages)} disabled={page === pages}
        onClick={() => onPage(Math.min(pages, page + 1))}>Next ›</button>
    </div>
  );
}

// ── StatBar ───────────────────────────────────────────────────────────────────
/**
 * Horizontal row of stat boxes (count summaries).
 *
 * stats: Array<{ label: string, value: number|string, color?: string }>
 */
export function StatBar({ stats, style: extra }) {
  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', ...extra }}>
      {stats.map(s => (
        <div key={s.label} style={{
          background:   '#fff',
          border:       `1px solid ${COLOR.grayBorder}`,
          borderRadius: RADIUS.md,
          padding:      '8px 16px',
          fontSize:     FONT.size.base,
          display:      'flex',
          alignItems:   'center',
          gap:          8,
        }}>
          <span style={{ fontWeight: FONT.weight.bold, fontSize: FONT.size.md, color: s.color || COLOR.blue }}>
            {s.value}
          </span>
          <span style={{ color: COLOR.textMuted }}>{s.label}</span>
        </div>
      ))}
    </div>
  );
}

// ── SearchBar ─────────────────────────────────────────────────────────────────
/**
 * Inline search input + optional slot for filters / buttons.
 *
 * <SearchBar value={q} onChange={setQ} placeholder="Search…">
 *   <Select .../>
 *   <Button>Clear</Button>
 * </SearchBar>
 */
export function SearchBar({ value, onChange, placeholder = 'Search…', children, style: extra }) {
  return (
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', ...extra }}>
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          padding:      '8px 12px',
          border:       `1px solid ${COLOR.grayBorder}`,
          borderRadius: RADIUS.md,
          fontSize:     FONT.size.base,
          fontFamily:   FONT.family,
          outline:      'none',
          background:   '#fff',
          minWidth:     200,
          flex:         '1 1 200px',
          maxWidth:     320,
        }}
      />
      {children}
    </div>
  );
}
